import * as Sentry from '@sentry/angular';

import { WorkflowStepErrorResponse } from '@shared/models';
import { classifyApiFailure, isPresignedUpload } from '@shared/utils/api-failure';

import { normalizeRoute } from './sentry.service';

export interface ApiFailure {
  status: number;
  method: string;
  url: string;
  /** The error response body: KOR's JSON, a plain string, or a ProgressEvent for a network failure */
  body: unknown;
  /** The app route the customer is on when the call fails (`router.url`) */
  route: string;
}

/**
 * True once the document starts going away (tab closed, navigated off, reloaded): the browser aborts its in-flight
 * calls as status 0, which is not an outage. `pagehide` also fires for a page restored from the back/forward cache, so
 * `pageshow` clears it.
 */
let pageUnloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => (pageUnloading = true));
  window.addEventListener('pageshow', () => (pageUnloading = false));
}

/** Status 0 while the page unloads or the device is offline is the customer leaving, not the service failing */
export const isClientGone = (): boolean => pageUnloading || (typeof navigator !== 'undefined' && navigator.onLine === false);

/** The title every failure of one kind shares, so Sentry groups on it and not on a stack frame or minified name */
export const apiFailureTitle = (status: number, endpoint: string): string => `API failure: ${status} ${endpoint}`;

const LINX_REQUEST_STEP = /^\/linx-request\/:id\/(?<step>.+)$/u;

/** The step the customer is on, e.g. `sign-here` or `individual/customer-account`; other routes with ids as `:id` */
export const workflowStepOf = (route: string): string => {
  const normalized = normalizeRoute(route);
  return LINX_REQUEST_STEP.exec(normalized)?.groups?.['step'] ?? normalized;
};

const bodyFields = (body: unknown): WorkflowStepErrorResponse & { traceId?: string } =>
  body && typeof body === 'object' ? (body as WorkflowStepErrorResponse & { traceId?: string }) : {};

/**
 * Reports a failed API call the backend cannot report itself (KOR-15194): a Sentry event, a breadcrumb, or nothing, as
 * `classifyApiFailure` decides. Sends the endpoint with ids as `:id` and no query string, the status, the errorCode, the
 * workflow step and the traceId - never the response `message` or body, which can carry customer data. Never throws: a
 * reporting failure must not change what the caller sees.
 */
export const reportApiFailure = ({ status, method, url, body, route }: ApiFailure): void => {
  try {
    const kind = classifyApiFailure(status, body, { clientGone: isClientGone(), presignedUpload: isPresignedUpload(method, url) });
    if (kind === 'none') return;

    const endpoint = `${method} ${normalizeRoute(url)}`;
    const { errorCode, traceId } = bodyFields(body);

    if (kind === 'breadcrumb') {
      Sentry.addBreadcrumb({ category: 'api-failure', level: 'warning', message: endpoint, data: { status, endpoint, traceId } });
      return;
    }

    // captureEvent, not captureMessage: `attachStacktrace` would add a synthetic stack, and Sentry then titles the issue
    // from its top frame - a minified function name such as `F1` - instead of from the message.
    Sentry.captureEvent({
      message: apiFailureTitle(status, endpoint),
      level: 'error',
      fingerprint: ['api-failure', String(status), endpoint, errorCode ?? '-'],
      tags: {
        apiStatus: String(status),
        apiEndpoint: endpoint,
        workflowStep: workflowStepOf(route),
        ...(errorCode ? { apiErrorCode: errorCode } : {}),
        // The join key to the backend's own event and logs for this request
        ...(traceId ? { traceId } : {}),
      },
    });
  } catch (error) {
    console.warn('Could not report a failed API call', error);
  }
};
